import {createApi} from '@reduxjs/toolkit/query/react';
import {AxiosRequestHeaders} from 'axios';
import {t} from '@lingui/core/macro';

import {IStatusPageView} from 'entities/statusView';

import httpBaseQuery from '../../../shared/api/rtk-query/httpBaseQuery';
import {ApiUrls} from '../../../shared/config/model/urls';
import IStatusPage, {ICheckSlugResponse, IStatusPageOutput} from '../model/StatusPage/IStatusPage';
import CreateStatusPageType from '../model/StatusPage/CreateStatusPageType';
import UpdateStatusPageType from '../model/StatusPage/UpdateStatusPageType';
import IHydraResponse from '../../../shared/api/model/HydraResponse';
import IPagination from '../../../shared/api/model/IPagination';
import {paginationAdapter} from '../../../shared/api/paginationHelper';
import {providesListUuid} from '../../../shared/api/rtk-query/RTKQueryHelper';
import ICollectionParams from '../../../shared/api/model/ICollectionParams';
import {buildQuery} from '../../../shared/api/query';

export const statusPageAPI = createApi({
    reducerPath: 'statusPageAPI',
    baseQuery: httpBaseQuery(),
    tagTypes: ['StatusPage'],
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        getStatusPages: build.query<IPagination<IStatusPage>, ICollectionParams>({
            query: ({page, sort, filters}) => ({
                path: buildQuery(ApiUrls.STATUS_PAGES(page, sort, filters)),
                headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
            }),
            providesTags: (result) => providesListUuid(result?.items, 'StatusPage'),
            transformResponse(
                rawResult: IHydraResponse<IStatusPage>,
            ): Promise<IPagination<IStatusPage>> | IPagination<IStatusPage> {
                return paginationAdapter(rawResult);
            },
        }),
        getStatusPage: build.query<IStatusPageOutput, string>({
            query: (uuid) => ({
                path: ApiUrls.STATUS_PAGE(uuid),
            }),
            providesTags: (result, error, uuid) => [{ type: 'StatusPage', id: uuid }],
        }),
        getStatusPageView: build.query<IStatusPageView, string>({
            query: (slugOrUuid) => ({
                path: ApiUrls.STATUS_PAGE_VIEW(slugOrUuid),
                isAnonymous: true,
            }),
        }),
        getStatusPagePdf: build.query<{url: string}, string>({
            query: (slugOrUuid) => ({
                path: ApiUrls.STATUS_PAGE_PDF(slugOrUuid),
                isAnonymous: true,
                errorMessage: t`Не удалось скачать PDF, попробуйте позже`,
            }),
        }),
        checkStatusPageSlug: build.query<ICheckSlugResponse, string>({
            query: (slug) => ({
                path: ApiUrls.STATUS_PAGE_CHECK_SLUG(slug),
                silent: true,
            }),
        }),
        addStatusPage: build.mutation<IStatusPageOutput, CreateStatusPageType>({
            query: (data) => ({
                path: ApiUrls.STATUS_PAGES(),
                method: 'POST',
                data,
            }),
            invalidatesTags: [{ type: 'StatusPage', id: 'LIST' }],
        }),
        updateStatusPage: build.mutation<IStatusPageOutput, UpdateStatusPageType>({
            query: ({uuid, ...data}) => ({
                path: ApiUrls.STATUS_PAGE(uuid),
                method: 'PUT',
                data,
            }),
            async onQueryStarted({ uuid, ...patch }, { dispatch }) {
                dispatch(
                    statusPageAPI.util.updateQueryData('getStatusPage', uuid, (draft) => {
                        Object.assign(draft, patch);
                    }),
                );
            },
            invalidatesTags: (result, error, {uuid}) => [
                { type: 'StatusPage', id: uuid },
                { type: 'StatusPage', id: 'LIST' },
            ],
        }),
        deleteStatusPage: build.mutation<void, string>({
            query: (uuid) => ({
                path: ApiUrls.STATUS_PAGE(uuid),
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'StatusPage', id: 'LIST' }],
        }),
    }),
});
